/**
 * Live SED source-role review — SED-resident leaf identity and UI binding.
 *
 * Browser-safe and pure (the hash is the dependency-free canonical SHA-256
 * used for report identities). The server review (Stage 2) and the UI both
 * derive `evidenceItemHash` from the SAME exact SED leaf + document source,
 * so a flag written for one SED can never attach to a leaf that a report
 * top-up, human review or rebuild has since changed.
 */
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type {
  SedSourceRoleLabel,
  SedSourceRoleLeafOrigin,
  SedSourceRoleReviewSidecar,
  SedSourceRoleReviewSidecarItem,
} from '../../types/sedSourceRoleReview';
import { hashCanonicalJson } from '../reportGeneration/reportContractHash';
import { flattenLeafValues } from './docConflicts';

export const SED_SOURCE_ROLE_SIDECAR_VERSION = 'sed-source-role-review-v1' as const;

/** One currently adopted SED leaf and the document source it was adopted from. */
export interface SedAdoptedDocumentLeaf {
  sectionId: string;
  /** Dot path relative to section data (`doc-field`) or auto-fill key (`auto-fill`). */
  fieldPath: string;
  origin: SedSourceRoleLeafOrigin;
  docId: string;
  value: string | number | boolean;
  excerptPreview?: string;
  sourceOffset?: { start: number; end: number };
  pageNumber?: number;
  attribution?: {
    appliesToProject?: 'this-site' | 'other-project' | 'unknown';
    statedByRole?: SourceRoleSpeaker;
    statedOn?: string;
  };
  requirementId?: string;
}

/**
 * Controlled speaker vocabulary for the second reader. `statedByRole` is
 * free model text (the extractor's "their title", up to 120 chars) and can
 * embed a person's or organisation's name, so only this coarse class ever
 * leaves the site record; an unrecognised title is dropped, never passed on.
 */
export type SourceRoleSpeaker = 'utility' | 'government-agency' | 'developer' | 'consultant' | 'landowner';

const SPEAKER_CLASSES: ReadonlyArray<readonly [SourceRoleSpeaker, RegExp]> = [
  ['landowner', /\b(?:land\s?owner|property\s+owner|seller|grantor|lessor)\b/i],
  ['utility', /\b(?:utilit(?:y|ies)|co-?op(?:erative)?|electric|transmission|distribution|interconnection|substation|key\s+accounts?|account\s+(?:manager|executive|representative)|director\s+of\s+water|water\s+(?:director|manager|superintendent|district|authority|company|utility|provider))\b/i],
  ['government-agency', /\b(?:county|city|municipal(?:ity)?|state|federal|agency|department|commission(?:er)?|planning|zoning|permit(?:ting)?|regulat(?:or|ory)|council|board|official|government|mayor|public\s+works)\b/i],
  ['consultant', /\b(?:consult(?:ants?|ing)|advis[eo]rs?|analysts?|attorneys?|counsel|lawyers?|brokers?|surveyors?|geologists?|hydrologists?)\b/i],
  ['developer', /\b(?:developers?|development|project\s+(?:manager|lead|director|sponsor)|sponsors?|applicants?|owner'?s?\s+representative|ceo|cfo|coo|cto|founder|president)\b/i],
];

/** Coarse speaker class of a free-text title, or undefined when it is not recognisable. */
export function sourceRoleSpeaker(value: unknown): SourceRoleSpeaker | undefined {
  if (typeof value !== 'string' || !value.trim()) return undefined;
  const text = value.slice(0, 200);
  return SPEAKER_CLASSES.find(([, pattern]) => pattern.test(text))?.[0];
}

type LooseSection = {
  status?: unknown;
  data?: unknown;
  carriedForward?: unknown;
  docFieldSources?: Record<string, {
    docId?: unknown; value?: unknown; excerptPreview?: unknown; pageNumber?: unknown;
    sourceOffset?: unknown; attribution?: Record<string, unknown>;
  }>;
  provenanceDetail?: {
    autoFillFields?: Record<string, unknown>;
    autoFillFieldSources?: Record<string, {
      docId?: unknown; value?: unknown; excerptPreview?: unknown; pageNumber?: unknown;
      sourceOffset?: unknown; attribution?: Record<string, unknown>; requirementId?: unknown;
    }>;
    fieldReviews?: Record<string, unknown>;
  };
};

const isScalar = (value: unknown): value is string | number | boolean =>
  typeof value === 'string' || typeof value === 'boolean' || (typeof value === 'number' && Number.isFinite(value));

function isSection(value: unknown): value is LooseSection {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
    && 'status' in (value as Record<string, unknown>)
    && ('docFieldSources' in (value as Record<string, unknown>) || 'provenanceDetail' in (value as Record<string, unknown>));
}

function offsetOf(value: unknown): { start: number; end: number } | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const { start, end } = value as { start?: unknown; end?: unknown };
  return Number.isInteger(start) && Number.isInteger(end) && (end as number) > (start as number)
    ? { start: start as number, end: end as number } : undefined;
}

/** Only non-identifying attribution fields ever leave this helper. */
function attributionOf(value: Record<string, unknown> | undefined): SedAdoptedDocumentLeaf['attribution'] {
  if (!value) return undefined;
  const out: NonNullable<SedAdoptedDocumentLeaf['attribution']> = {};
  const applies = value.appliesToProject;
  if (applies === 'this-site' || applies === 'other-project' || applies === 'unknown') out.appliesToProject = applies;
  const speaker = sourceRoleSpeaker(value.statedByRole);
  if (speaker) out.statedByRole = speaker;
  if (typeof value.statedOn === 'string' && value.statedOn.trim()) out.statedOn = value.statedOn.trim().slice(0, 40);
  return Object.keys(out).length ? out : undefined;
}

/**
 * Adopted document leaves whose CURRENT value still equals the source value
 * (the `sedToText` document-field-source rule). Excludes carried-forward
 * sections, human-reviewed auto-fill keys and in-band amended leaves: a
 * human decision is authority and is never second-read.
 */
export function adoptedDocumentLeaves(sed: SiteEvidenceDocument | null | undefined): SedAdoptedDocumentLeaf[] {
  if (!sed) return [];
  const amended = new Set((Array.isArray(sed.amendments) ? sed.amendments : [])
    .map(row => (row && typeof row.field === 'string' ? row.field : '')));
  const out: SedAdoptedDocumentLeaf[] = [];
  for (const [sectionId, raw] of Object.entries(sed as unknown as Record<string, unknown>)) {
    if (!isSection(raw) || raw.carriedForward) continue;
    const leaves = flattenLeafValues(raw.data);
    for (const [fieldPath, source] of Object.entries(raw.docFieldSources ?? {})) {
      if (!source || typeof source.docId !== 'string' || !isScalar(source.value)) continue;
      if (leaves[fieldPath] !== source.value || amended.has(`${sectionId}.data.${fieldPath}`)) continue;
      out.push({
        sectionId, fieldPath, origin: 'doc-field', docId: source.docId, value: source.value,
        ...(typeof source.excerptPreview === 'string' ? { excerptPreview: source.excerptPreview } : {}),
        ...(offsetOf(source.sourceOffset) ? { sourceOffset: offsetOf(source.sourceOffset) } : {}),
        ...(typeof source.pageNumber === 'number' ? { pageNumber: source.pageNumber } : {}),
        ...(attributionOf(source.attribution) ? { attribution: attributionOf(source.attribution) } : {}),
      });
    }
    const detail = raw.provenanceDetail;
    for (const [key, source] of Object.entries(detail?.autoFillFieldSources ?? {})) {
      if (!source || typeof source.docId !== 'string' || !isScalar(source.value)) continue;
      if (detail?.fieldReviews?.[key] || detail?.autoFillFields?.[key] !== source.value) continue;
      out.push({
        sectionId, fieldPath: key, origin: 'auto-fill', docId: source.docId, value: source.value,
        ...(typeof source.excerptPreview === 'string' ? { excerptPreview: source.excerptPreview } : {}),
        ...(offsetOf(source.sourceOffset) ? { sourceOffset: offsetOf(source.sourceOffset) } : {}),
        ...(typeof source.pageNumber === 'number' ? { pageNumber: source.pageNumber } : {}),
        ...(attributionOf(source.attribution) ? { attribution: attributionOf(source.attribution) } : {}),
        ...(typeof source.requirementId === 'string' ? { requirementId: source.requirementId } : {}),
      });
    }
  }
  return out;
}

/** SED-resident identity of one adopted leaf and its exact document source. */
export function sedLeafEvidenceItemHash(leaf: Pick<SedAdoptedDocumentLeaf,
  'sectionId' | 'fieldPath' | 'origin' | 'docId' | 'value' | 'excerptPreview' | 'pageNumber'>): string {
  return hashCanonicalJson({
    v: 'sed-source-role-leaf-v1',
    sectionId: leaf.sectionId,
    fieldPath: leaf.fieldPath,
    origin: leaf.origin,
    docId: leaf.docId,
    value: leaf.value,
    excerptPreview: leaf.excerptPreview ?? null,
    pageNumber: leaf.pageNumber ?? null,
  });
}

/** UI key: variable-browser rows use `${sectionId}.data.${fieldPath}`. */
export function sourceRoleFlagKey(item: Pick<SedSourceRoleReviewSidecarItem, 'sectionId' | 'fieldPath' | 'origin'>): string {
  return item.origin === 'doc-field'
    ? `${item.sectionId}.data.${item.fieldPath}`
    : `${item.sectionId}.autoFill.${item.fieldPath}`;
}

/** Plain-language label meanings for the neutral tooltip. */
export const SED_SOURCE_ROLE_LABEL_MEANINGS: Readonly<Record<SedSourceRoleLabel, string>> = {
  'supports-field': 'The source passage supports this value for the field as defined.',
  'status-overstated': 'The source may present this as requested, planned, tentative or conditional rather than established or committed.',
  'other-subject-or-scope': 'The source may describe another project, a system-wide or regional figure, or a broader feature rather than this site.',
  'different-quantity-or-date-basis': 'The source may state a different quantity, unit basis or kind of date than this field records.',
  'not-established': 'The source passage did not settle this field either way.',
};

export interface BoundSourceRoleFlag {
  key: string;
  sectionId: string;
  fieldPath: string;
  origin: SedSourceRoleLeafOrigin;
  label: SedSourceRoleLabel;
  confidence: number;
  meaning: string;
  /** When the second reader ran; a human review record after this supersedes the chip. */
  reviewedAt?: string;
}

/**
 * Only labels that suggest a review may bind. `supports-field` is never
 * written as a flag, and rendering it as "review suggested" beside a
 * "supports this value" meaning would read as a verification.
 */
const FLAG_LABELS: ReadonlySet<string> = new Set<SedSourceRoleLabel>([
  'status-overstated', 'other-subject-or-scope', 'different-quantity-or-date-basis', 'not-established',
]);

/**
 * Flags that still bind to the displayed SED: the sidecar names this exact
 * SED id, and every returned item's `evidenceItemHash` equals the hash of a
 * currently adopted leaf, carries a review-suggesting label and a finite
 * confidence. Anything malformed or stale is dropped silently — the chip is
 * advisory and never counted anywhere.
 */
export function bindSedSourceRoleFlags(
  sed: SiteEvidenceDocument | null | undefined,
  sidecar: unknown,
): Map<string, BoundSourceRoleFlag> {
  const bound = new Map<string, BoundSourceRoleFlag>();
  if (!sed || !sidecar || typeof sidecar !== 'object') return bound;
  const candidate = sidecar as Partial<SedSourceRoleReviewSidecar>;
  if (candidate.version !== SED_SOURCE_ROLE_SIDECAR_VERSION || candidate.sedId !== sed.id || !Array.isArray(candidate.items)) {
    return bound;
  }
  const current = new Map(adoptedDocumentLeaves(sed).map(leaf => [sedLeafEvidenceItemHash(leaf), leaf]));
  const reviewedAt = typeof candidate.reviewedAt === 'string' ? candidate.reviewedAt : undefined;
  for (const item of candidate.items) {
    if (!item || typeof item !== 'object' || !FLAG_LABELS.has(item.label)) continue;
    if (typeof item.confidence !== 'number' || !Number.isFinite(item.confidence) || item.confidence < 0 || item.confidence > 1) continue;
    const leaf = current.get(item.evidenceItemHash);
    if (!leaf || leaf.sectionId !== item.sectionId || leaf.fieldPath !== item.fieldPath || leaf.origin !== item.origin) continue;
    const key = sourceRoleFlagKey(item);
    bound.set(key, {
      key, sectionId: item.sectionId, fieldPath: item.fieldPath, origin: item.origin,
      label: item.label, confidence: item.confidence,
      meaning: SED_SOURCE_ROLE_LABEL_MEANINGS[item.label],
      ...(reviewedAt ? { reviewedAt } : {}),
    });
  }
  return bound;
}
